import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Users, Search, Link2, CheckCircle2, RefreshCw } from "lucide-react";

export function StudentPicker({ onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(async () => {
      if (!q.trim()) { setItems([]); return; }
      setLoading(true);
      try {
        const { data } = await api.get(`/admin/students?q=${encodeURIComponent(q.trim())}`);
        setItems(data.students || []);
        setOpen(true);
      } catch { /* noop */ }
      setLoading(false);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div className="relative" ref={boxRef}>
      <div className="flex items-center gap-2 h-10 px-3 rounded-xl border border-[#E5E0D8] focus-within:border-[#E25E3E] bg-white">
        <Search className="h-4 w-4 text-[#9C9A94]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} onFocus={() => q && setOpen(true)}
          data-testid="student-search-input" placeholder="Cari nama akun siswa…"
          className="flex-1 outline-none text-sm bg-transparent" />
        {loading && <RefreshCw className="h-3.5 w-3.5 text-[#9C9A94] animate-spin" />}
      </div>
      {open && items.length > 0 && (
        <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-xl border border-[#E5E0D8] bg-white shadow-lg">
          {items.map((s) => (
            <button key={s.id} type="button" data-testid={`student-option-${s.id}`}
              onClick={() => { onPick(s); setOpen(false); setQ(s.name); }}
              className="w-full text-left px-3 py-2 hover:bg-[#F7F4EE] transition-colors">
              <div className="text-sm font-medium text-[#1A1C1A]">{s.name}</div>
              <div className="text-xs text-[#9C9A94]">{s.email}{s.kelas ? ` · Kelas ${s.kelas}` : ""}</div>
            </button>
          ))}
        </div>
      )}
      {open && !loading && q.trim() && items.length === 0 && (
        <div className="absolute z-20 mt-1 w-full rounded-xl border border-[#E5E0D8] bg-white shadow-lg px-3 py-2 text-sm text-[#9C9A94]">
          Tidak ada akun cocok
        </div>
      )}
    </div>
  );
}

function UnmatchedRow({ batchId, nama, assignUrl, onAssigned }) {
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const assign = async () => {
    if (!picked) { toast.error("Pilih akun siswa dulu"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(assignUrl, { batch_id: batchId, nama, user_id: picked.id });
      toast.success(`"${nama}" → ${data.assigned_to}`);
      setDone(true);
      onAssigned && onAssigned();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal mencocokkan");
    }
    setBusy(false);
  };

  if (done) return (
    <div className="flex items-center gap-2 py-2 text-sm text-[#4A6B53]" data-testid={`assigned-${nama}`}>
      <CheckCircle2 className="h-4 w-4" /> <b>{nama}</b> sudah dicocokkan ke {picked?.name}
    </div>
  );

  return (
    <div className="grid sm:grid-cols-[1fr_1.4fr_auto] gap-2 items-center py-2 border-b border-[#F0ECE4] last:border-0">
      <div className="text-sm font-medium text-[#1A1C1A]">{nama}</div>
      <StudentPicker onPick={setPicked} />
      <button onClick={assign} disabled={busy || !picked} data-testid={`assign-btn-${nama}`}
        className="px-4 py-2 rounded-full bg-[#1A1C1A] text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-40 hover:bg-black transition-colors">
        <Link2 className="h-3.5 w-3.5" /> Cocokkan
      </button>
    </div>
  );
}

const KIND_CFG = {
  tryout: { batches: "/admin/tryout/batches", assign: "/admin/tryout/assign", desc: "Nama peserta TryOut yang tak otomatis cocok dengan akun. Pilih akun siswa yang benar untuk menyalurkan hasilnya." },
  recognize: { batches: "/admin/recognize/batches", assign: "/admin/recognize/assign", desc: "Nama peserta Recognize/Baseline yang belum cocok. Pilih akun siswa yang benar." },
  posttest: { batches: "/admin/posttest/batches", assign: "/admin/posttest/assign", desc: "Nama peserta Postes Harian yang belum cocok. Pilih akun siswa yang benar." },
};

export default function ManualMatch({ refreshKey, kind = "tryout" }) {
  const cfg = KIND_CFG[kind] || KIND_CFG.tryout;
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(cfg.batches);
      setBatches(data.batches || []);
    } catch { /* noop */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const pending = batches.filter((b) => (b.unmatched_count || 0) > 0);

  return (
    <div className="clay-card p-6 mt-6" data-testid="manual-match-panel">
      <div className="flex items-center justify-between mb-1">
        <h2 className="font-heading font-bold text-lg text-[#1A1C1A] flex items-center gap-2">
          <Users className="h-5 w-5 text-[#E25E3E]" /> Pencocokan Nama Manual
        </h2>
        <button onClick={load} data-testid="refresh-batches-btn" className="text-sm text-[#5C5F5C] inline-flex items-center gap-1 hover:text-[#1A1C1A]">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Muat ulang
        </button>
      </div>
      <p className="text-sm text-[#5C5F5C] mb-4">{cfg.desc}</p>

      {loading && <p className="text-sm text-[#9C9A94]">Memuat…</p>}
      {!loading && pending.length === 0 && (
        <p className="text-sm text-[#4A6B53]" data-testid="no-unmatched">Semua peserta sudah tercocokkan. 🎉</p>
      )}

      <div className="space-y-5">
        {pending.map((b) => (
          <div key={b.id} data-testid={`batch-unmatched-${b.id}`}>
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-[#1A1C1A]">{b.batch_name}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full bg-[#D9381E]/10 text-[#D9381E]">{b.unmatched_count} belum cocok</span>
            </div>
            <div className="rounded-xl border border-[#E5E0D8] px-4">
              {(b.unmatched_list || []).map((nama, i) => (
                <UnmatchedRow key={i} batchId={b.id} nama={nama} assignUrl={cfg.assign} onAssigned={load} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
